import { useState } from "react";
import { Button, InlineAlert, LoadingSpinner } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import CurrencyInput from "../../components/BaseComponents/Forms/CurrencyInput";
import Input from "../../components/BaseComponents/Forms/Input";
import Label from "../../components/BaseComponents/Label";
import useApi from "../../hooks/useApi";
import { currencyToCents, getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type CompanyProjectCreateModalProps = {
    open: boolean;
    onClose: () => void;
}

function CompanyProjectCreateModal({open, onClose}: CompanyProjectCreateModalProps) {
    const [loading, setLoading] = useState(false);
    // Issue #884: onCreate previously had no catch at all, so a rejecting write (including the
    // now-rejecting READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE notify-only codes - #857 item 6)
    // produced an unhandled rejection, left `loading` stuck true (spinner forever), and never
    // closed/reset the modal - but also never told the admin anything had gone wrong.
    const [error, setError] = useState('');
    const api = useApi();

    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [turnover, setTurnover] = useState(0);

    const reset = () => {
        setName('');
        setCode('');
        setTurnover(0);
        setError('');
    };

    const onCreate = async () => {
        setLoading(true);
        setError('');
        try {
            await api.post('/projects', {
                name,
                code,
                totalTurnoverAmount: turnover,
                _csrf: await getCSRFToken(),
            });
        } catch (err: any) {
            // Standard pattern (see DeleteTrainingSessionModal/AdvisorsTable) - a visible,
            // existing-shape message, not a raw/logged error. The modal stays open (unlike
            // success) so the admin can retry or cancel without losing what they typed.
            setError(getApiErrorMessage(err, 'Failed to create this project. Please try again.'));
            return;
        } finally {
            setLoading(false);
        }
        reset();
        onClose();
    }

    return (
        <CanonicalModal
            open={open}
            onClose={() => { reset(); onClose(); }}
            size="small"
            title="Create Project"
            actions={
                <>
                    <Button onClick={onCreate} additionalClass="mr-5" disabled={!name || !code} type="Primary" text="Create" />
                    <Button onClick={onClose} type="Outlined" text="Cancel" />
                </>
            }
        >
            { error && <InlineAlert type='error' className='mb-4'>{ error }</InlineAlert> }
            { loading ? <LoadingSpinner /> : (
                <form>
                    <Input
                        label="Project name"
                        name="name"
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        additionalClass="mt-8"
                    />
                    <Input
                        label="Unique project code"
                        name="code"
                        type="text"
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        additionalClass="mt-8"
                    />
                    <div className="mt-8">
                        <Label className="block">Project value (total sales/revenues)</Label>
                        <CurrencyInput name="turnover" onChange={value => setTurnover(currencyToCents(value))} />
                    </div>
                </form>
            )}
        </CanonicalModal>
    )
}

export default CompanyProjectCreateModal;
